import {
  agentConfigurationSummarySchema,
  agentConfigurationActionSchema,
  createAgentConfigurationDraftSchema,
  scopeSchema,
  type AgentConfigurationActionInput,
  type CreateAgentConfigurationDraftInput,
} from '@eairlines/contracts';
import { z } from 'zod';
import { request } from './client';
import { assertCurrentSession, getSession } from '../auth/session';

export type ConfigurationAction = 'submit' | 'approve' | 'reject' | 'publish' | 'rollback';
const path = (agentId: string) => `/agents/${encodeURIComponent(agentId)}/configuration`;

function assertWritable() {
  if (import.meta.env.VITE_READ_ONLY_PREVIEW === 'true') {
    throw new Error('Configuration changes are unavailable in read-only preview.');
  }
}

export async function getAgentConfiguration(agentId: string) {
  return agentConfigurationSummarySchema.parse(await request(path(agentId)));
}

export async function getAgentConfigurationScopes(agentId: string) {
  return scopeSchema.extend({ id: z.string().uuid() }).array().parse(await request(`${path(agentId)}/scopes`));
}

export async function saveAgentConfiguration(agentId: string, input: CreateAgentConfigurationDraftInput) {
  assertWritable();
  const origin = getSession();
  assertCurrentSession(origin);
  agentConfigurationSummarySchema.parse(await request(`${path(agentId)}/drafts`, {
    method: 'POST', body: JSON.stringify(createAgentConfigurationDraftSchema.parse(input)),
  }));
  assertCurrentSession(origin);
  const confirmed = await getAgentConfiguration(agentId);
  assertCurrentSession(origin);
  return confirmed;
}

export async function transitionAgentConfiguration(
  agentId: string, version: number, action: ConfigurationAction, input: AgentConfigurationActionInput,
) {
  assertWritable();
  const origin = getSession();
  assertCurrentSession(origin);
  agentConfigurationSummarySchema.parse(await request(`${path(agentId)}/versions/${version}/${action}`, {
    method: 'POST', body: JSON.stringify(agentConfigurationActionSchema.parse(input)),
  }));
  assertCurrentSession(origin);
  const confirmed = await getAgentConfiguration(agentId);
  assertCurrentSession(origin);
  return confirmed;
}
